import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query'
import { disableEinvoicing } from '../businesses'
import { useBusinessContext } from '@root/context'
import toaster from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useRef } from 'react'
import {
  fetchEInvoiceAddresses,
  fetchEInvoiceOperators,
  IEInvoiceAddress,
  IEInvoiceOperator
} from '../integrations/apix'
import { stripeBillingQueryKeys } from './stripeBillingQueryKeys'

const BUSINESS_ID_REGEX = /^\d{7}-\d$/

export const useDisableEinvoicing = (): UseMutationResult<unknown, unknown, void> => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()

  const mutation = useMutation({
    mutationFn: () => disableEinvoicing({ slug }),
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['businesses'] }),
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: stripeBillingQueryKeys.all(slug) })
        ])
        toaster.success(t('general.changesSaved'))
      },
      onError: () => {
        toaster.error(t('general.error'))
      }
    }
  })

  return mutation
}

export const useEInvoiceOperators = () => {
  const { slug } = useBusinessContext()

  return useQuery<IEInvoiceOperator[]>({
    queryKey: [slug, 'einvoice-operators'],
    queryFn: () => fetchEInvoiceOperators({ slug }),
    enabled: Boolean(slug),
    staleTime: 24 * 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
}

export const useEInvoiceAddressQuery = (
  businessId: string | undefined,
  enabled: boolean,
  onFetched: (data: IEInvoiceAddress[]) => void
) => {
  const { slug, data: business } = useBusinessContext()
  const isFinnishBusiness = business?.country?.toLowerCase() === 'fi'
  const isValidBusinessId = BUSINESS_ID_REGEX.test(businessId ?? '')
  const onFetchedRef = useRef(onFetched)
  onFetchedRef.current = onFetched

  return useQuery<IEInvoiceAddress[]>({
    queryKey: [slug, 'einvoice-address-query', businessId],
    queryFn: async () => {
      const data = await fetchEInvoiceAddresses({ slug, businessId: businessId! })
      onFetchedRef.current(data)
      return data
    },
    enabled:
      enabled && isFinnishBusiness && isValidBusinessId && Boolean(business?.einvoicing_enabled),
    staleTime: 5 * 60 * 1000,
    gcTime: 0
  })
}
